// ManualTargetsScreen.jsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";

import EChartsReact from "echarts-for-react";
import Axios from "axios";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
    getHotelName,
    HOTELS_INFO,
    SanitizeHotelID,
    URLAPI,
} from "../configuration";

import HoverPaper from "../components/shared/HoverPaper";
import DateRangePicker from "../components/DateRangePicker";
import DataLoader from "../components/DataLoader";

import {
    loading_ManualTargets,
    load_ManualTargets,
    error_ManualTargets,
    clear_ManualTargets,
} from "../features/targets/dataTargetsSlice";

import Icons from "../helpers/icons";
import Colours from "../helpers/colours";
import {
    COMPARISON_PREVIOUS_YEAR,
    DATATYPE_CHECKIN,
    DATATYPE_CHECKOUT,
    DATATYPE_RESDATE,
    getMetricName,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    process_,
} from "../helpers/filters";
import { MAX_DATE, MIN_DATE } from "../helpers/dates";
import {
    getActivePeriodForDate,
    getInterpolatedManualValue,
    getLastYearValue,
    getPeriodDistance,
    getStepUnit,
    MANUAL_TARGET_PERIODS_URL,
    MANUAL_TARGETS_URL,
    normalizePointDate,
    normalizeValidFromDate,
    TARGET_DATE_TYPES,
    TARGET_GRANULARITY_DAY,
    TARGET_GRANULARITY_MONTH,
    TARGET_GRANULARITY_YEAR,
    TARGET_MODES,
    TARGET_SUBTYPES,
    TARGET_TYPES,
} from "../helpers/targets";
import ManualTargetsVisualizer, {
    ManualTargetsViewModeToggle,
} from "../components/ManualTargetsVisualizer";
import { selectHotels } from "../features/hotels/hotelsApi";

dayjs.extend(utc);

const DEFAULT_PERCENTAGE = 5;

const round2 = (value) => Number(Number(value ?? 0).toFixed(2));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getErrorMessage = (error, fallback) => {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};

const getPeriodPickerProps = (dateGranularity) => {
    if (dateGranularity === TARGET_GRANULARITY_MONTH) {
        return {
            format: "MMM YYYY",
            views: ["year", "month"],
            openTo: "month",
        };
    }

    if (dateGranularity === TARGET_GRANULARITY_YEAR) {
        return {
            format: "YYYY",
            views: ["year"],
            openTo: "year",
        };
    }

    return {
        format: "DD MMM YYYY",
    };
};

const ValidFromPicker = ({ value, onChange, dateGranularity, label, sx }) => {
    const [open, setOpen] = useState(false);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                timezone="UTC"
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={value ? dayjs.utc(value) : null}
                minDate={dayjs.utc(MIN_DATE)}
                maxDate={dayjs.utc(MAX_DATE)}
                onChange={(newValue) => {
                    if (!newValue) return;

                    const selected = newValue.utc();
                    let nextValue;

                    if (dateGranularity === TARGET_GRANULARITY_MONTH) {
                        nextValue = dayjs
                            .utc()
                            .year(selected.year())
                            .month(selected.month())
                            .startOf("month");
                    } else if (dateGranularity === TARGET_GRANULARITY_YEAR) {
                        nextValue = dayjs
                            .utc()
                            .year(selected.year())
                            .month(0)
                            .date(1)
                            .startOf("day");
                    } else {
                        nextValue = dayjs
                            .utc()
                            .year(selected.year())
                            .month(selected.month())
                            .date(selected.date())
                            .startOf("day");
                    }

                    onChange(nextValue.format("YYYY-MM-DD"));
                }}
                slotProps={{
                    popper: {
                        keepMounted: true,
                        // Let the calendar render outside the dialog
                        disablePortal: false,
                    },
                    textField: {
                        label,
                        size: "small",
                        fullWidth: true,
                        onClick: () => setOpen(true),
                        sx,
                        InputProps: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icons.Calendar />
                                </InputAdornment>
                            ),
                            endAdornment: null,
                            inputProps: {
                                readOnly: true,
                            },
                        },
                    },
                }}
                {...getPeriodPickerProps(dateGranularity)}
            />
        </LocalizationProvider>
    );
};

const asUtcDay = (value) => {
    if (!value) return null;
    return dayjs.isDayjs(value) ? value.utc() : dayjs.utc(value);
};

const normalizeRangeStart = (value, dateGranularity) => {
    const base = asUtcDay(value);
    if (!base) return null;

    if (dateGranularity === TARGET_GRANULARITY_MONTH)
        return base.startOf("month");
    if (dateGranularity === TARGET_GRANULARITY_YEAR)
        return base.startOf("year");
    return base.startOf("day");
};

const normalizeRangeEnd = (value, dateGranularity) => {
    const base = asUtcDay(value);
    if (!base) return null;

    if (dateGranularity === TARGET_GRANULARITY_MONTH)
        return base.endOf("month");
    if (dateGranularity === TARGET_GRANULARITY_YEAR) return base.endOf("year");
    return base.startOf("day");
};

const formatDisplayDate = (value, dateGranularity) => {
    const date = asUtcDay(value);
    if (!date) return "";

    if (dateGranularity === TARGET_GRANULARITY_MONTH)
        return date.format("MMM YYYY");
    if (dateGranularity === TARGET_GRANULARITY_YEAR) return date.format("YYYY");
    return date.format("DD MMM YYYY");
};

const formatTooltipNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }

    return round2(value);
};

const buildAxisDates = (fromDate, toDate, dateGranularity) => {
    const start = normalizeRangeStart(fromDate, dateGranularity);
    const end = normalizeRangeEnd(toDate, dateGranularity);

    if (!start || !end || end.isBefore(start, "day")) return [];

    const unit = getStepUnit(dateGranularity);
    const dates = [];
    let cursor = start;

    while (!cursor.isAfter(end, unit)) {
        if (dateGranularity === TARGET_GRANULARITY_MONTH) {
            dates.push(cursor.startOf("month"));
        } else if (dateGranularity === TARGET_GRANULARITY_YEAR) {
            dates.push(cursor.startOf("year"));
        } else {
            dates.push(cursor.startOf("day"));
        }

        cursor = cursor.add(1, unit);
    }

    return dates;
};

const getModeLabel = (modeValue) => {
    const match = TARGET_MODES.find(
        (option) =>
            option.value === String(modeValue).toLowerCase() ||
            option.apiValue === String(modeValue).toUpperCase(),
    );

    return match?.label ?? modeValue;
};

const getModeApiValue = (modeValue) => {
    return (
        TARGET_MODES.find((option) => option.value === modeValue)?.apiValue ??
        "MANUAL"
    );
};

const calculateLastYear = (
    dataBookings,
    dataSimple,
    dataTargets,
    fromDay,
    toDay,
    dateMode,
    type,
    hotelID,
) => {
    let dataType;
    switch (type.toUpperCase()) {
        case "CHECKIN":
            dataType = DATATYPE_CHECKIN;
            break;
        case "CHECKOUT":
            dataType = DATATYPE_CHECKOUT;
            break;
        case "RESDATE":
        default:
            dataType = DATATYPE_RESDATE;
    }

    let groupBy;
    switch (dateMode) {
        case TARGET_GRANULARITY_YEAR:
            groupBy = [GROUP_DATE_YEAR];
            break;
        case TARGET_GRANULARITY_MONTH:
            groupBy = [GROUP_DATE_MONTH, GROUP_DATE_YEAR];
            break;
        case TARGET_GRANULARITY_DAY:
        default:
            groupBy = [GROUP_DATE_DAY, GROUP_DATE_MONTH, GROUP_DATE_YEAR];
    }

    const fromDayNormalized = dayjs.utc(fromDay).format("YYYY-MM-DD");
    const toDayNormalized = dayjs.utc(toDay).format("YYYY-MM-DD");

    let js = {
        dataType,
        fromDay: fromDayNormalized,
        toDay: toDayNormalized,
        fromArrivalDay: MIN_DATE,
        toArrivalDay: MAX_DATE,
        fromResDateDay: MIN_DATE,
        toResDateDay: MAX_DATE,
        filters: {
            Comparison: COMPARISON_PREVIOUS_YEAR,
        },
        groupBy,
    };

    let p = process_({
        js,
        dataBookings,
        hotelID,
        dataSimple,
        dataTargets,
    });

    return p;
};

const getModeFormValue = (modeValue) => {
    return (
        TARGET_MODES.find(
            (option) =>
                option.value === String(modeValue).toLowerCase() ||
                option.apiValue === String(modeValue).toUpperCase(),
        )?.value ?? "manual"
    );
};

const TARGET_DATE_TYPE_ORDER = TARGET_DATE_TYPES.reduce((acc, item, index) => {
    acc[item.value] = index;
    return acc;
}, {});

const getDateGranularityLabel = (value) => {
    return (
        TARGET_DATE_TYPES.find((item) => item.value === value)?.label ?? value
    );
};

const getTargetTypeLabel = (value) => {
    return TARGET_TYPES.find((item) => item.value === value)?.label ?? value;
};

const getTargetSubtypeLabel = (value) => getMetricName(value) ?? value;

const isCountNodeActive = (selection, currentSelection) => {
    if (!selection || !currentSelection) return false;

    const sameBaseSelection =
        selection.targetType === currentSelection.targetType &&
        selection.targetSubtype === currentSelection.targetSubtype &&
        selection.dateGranularity === currentSelection.dateGranularity;

    if (!sameBaseSelection) return false;

    // Manual points do not include targetMode, so matching the base filters is enough
    if (!selection.targetMode) return true;

    return (
        getModeFormValue(selection.targetMode) === currentSelection.targetMode
    );
};

const CountLeafRow = ({
    label,
    count,
    onClick,
    clickable = false,
    active = false,
}) => {
    return (
        <Box
            onClick={clickable ? onClick : undefined}
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 1,
                px: 1.25,
                py: 0.9,
                minHeight: 40,
                cursor: clickable ? "pointer" : "default",
                transition: "0.15s ease",
                "&:hover": clickable
                    ? {
                          backgroundColor: "rgba(255,255,255,0.04)",
                          borderColor: "rgba(255,255,255,0.24)",
                      }
                    : undefined,
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    textDecoration: clickable ? "underline" : "none",
                    color: active ? "primary.main" : "inherit",
                    fontWeight: active ? 700 : 400,
                }}
            >
                {clickable && (
                    <Icons.OpenLink
                        sx={{
                            fontSize: "1rem",
                            ml: 1,
                            mr: 2,
                            color: active ? "primary.main" : "inherit",
                        }}
                    />
                )}
                {label}
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    color: active ? "primary.main" : "inherit",
                }}
            >
                {count}
            </Typography>
        </Box>
    );
};

const CountAccordionNode = ({
    node,
    nested = false,
    onSelect,
    currentSelection,
}) => {
    const hasChildren =
        Array.isArray(node.children) && node.children.length > 0;

    if (!hasChildren) {
        return (
            <CountLeafRow
                label={node.label}
                count={node.count}
                clickable={!!node.selection}
                active={isCountNodeActive(node.selection, currentSelection)}
                onClick={() => onSelect?.(node)}
            />
        );
    }

    return (
        <Accordion
            disableGutters
            elevation={0}
            sx={{
                backgroundColor: "transparent",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "8px !important",
                "&:before": {
                    display: "none",
                },
            }}
        >
            <AccordionSummary
                expandIcon={<Icons.ArrowDown />}
                sx={{
                    minHeight: 44,
                    "& .MuiAccordionSummary-content": {
                        my: 1,
                    },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pr: 1,
                        gap: 2,
                    }}
                >
                    <Typography
                        sx={{ fontWeight: 700 }}
                        variant={nested ? "body2" : "subtitle2"}
                    >
                        {node.label}
                    </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {node.count}
                    </Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, pb: 1.25 }}>
                <Stack spacing={1}>
                    {node.children.map((child) => (
                        <CountAccordionNode
                            key={child.key}
                            node={child}
                            nested
                            onSelect={onSelect}
                            currentSelection={currentSelection}
                        />
                    ))}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

const ManualTargetsScreen = () => {
    const dispatch = useDispatch();
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const sanitizedHotelID = useMemo(() => SanitizeHotelID(hotelID), [hotelID]);
    const authUser = useSelector((state) => state.auth?.user);
    const { dataBookings, dataSimple, dataTargets } = useSelector(
        (state) => state,
    );
    const { data: hotels = [] } = useSelector(selectHotels);
    console.log("dataTargets", dataTargets);

    const userIdForTargets = authUser?.user_id;
    const [visualizerMode, setVisualizerMode] = useState("chart");

    const headers = useMemo(() => {
        return {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authUser?.jwt ?? ""}`,
        };
    }, [authUser?.jwt]);

    const hotelTargets = useMemo(() => {
        if (!Array.isArray(dataTargets?.data)) return [];

        return [...dataTargets.data].sort((left, right) => {
            const leftYear = Number(
                left.year_v ?? left.Year_V ?? left.year ?? 0,
            );
            const rightYear = Number(
                right.year_v ?? right.Year_V ?? right.year ?? 0,
            );

            if (leftYear !== rightYear) return rightYear - leftYear;

            const leftMonth = Number(
                left.month_v ?? left.Month_V ?? left.month ?? 0,
            );
            const rightMonth = Number(
                right.month_v ?? right.Month_V ?? right.month ?? 0,
            );

            if (leftMonth !== rightMonth) return rightMonth - leftMonth;

            const leftName = String(left.name ?? left.Name ?? "");
            const rightName = String(right.name ?? right.Name ?? "");

            return leftName.localeCompare(rightName);
        });
    }, [dataTargets?.data]);

    // const targetsSummaryText = useMemo(() => {
    //     if (dataTargets?.status === "loading")
    //         return "Loading hotel targets...";
    //     if (dataTargets?.status === "error")
    //         return "Could not load hotel targets.";
    //     if (!hotelTargets.length) return "No hotel targets loaded.";

    //     return `${hotelTargets.length} hotel targets loaded.`;
    // }, [dataTargets?.status, hotelTargets.length]);

    const [targetType, setTargetType] = useState(TARGET_TYPES[0].value);
    const [targetSubtype, setTargetSubtype] = useState(
        TARGET_SUBTYPES[0].value,
    );
    const [dateGranularity, setDateGranularity] = useState(
        TARGET_GRANULARITY_DAY,
    );

    const [chartFromDate, setChartFromDate] = useState(
        dayjs.utc().startOf("year"),
    );
    const [chartToDate, setChartToDate] = useState(dayjs.utc().endOf("year"));

    const [validFrom, setValidFrom] = useState(
        dayjs.utc().startOf("day").format("YYYY-MM-DD"),
    );
    const [targetMode, setTargetMode] = useState("manual");
    const [percentageN, setPercentageN] = useState(DEFAULT_PERCENTAGE);
    const [savedTargetsScope, setSavedTargetsScope] = useState("range");

    // const [manualTargets, setManualTargets] = useState([]);
    // const [periodRows, setPeriodRows] = useState([]);

    const currentManualFiltersKey = useMemo(
        () =>
            JSON.stringify({
                hotelID: sanitizedHotelID,
                targetType: targetType.trim(),
                targetSubtype:
                    visualizerMode === "table" ? null : targetSubtype.trim(),
                dateGranularity,
                visualizerMode,
            }),
        [
            sanitizedHotelID,
            targetType,
            targetSubtype,
            dateGranularity,
            visualizerMode,
        ],
    );

    const hasCurrentManualTargetsLoaded =
        dataTargets?.manual?.hotelID === sanitizedHotelID &&
        dataTargets?.manual?.filtersKey === currentManualFiltersKey;

    const manualTargets = hasCurrentManualTargetsLoaded
        ? (dataTargets?.manual?.manualTargets ?? [])
        : [];

    const periodRows = hasCurrentManualTargetsLoaded
        ? (dataTargets?.manual?.periodRows ?? [])
        : [];

    const manualTargetsCounts = hasCurrentManualTargetsLoaded
        ? (dataTargets?.manual?.manualTargetsCounts ?? [])
        : [];

    const periodCounts = hasCurrentManualTargetsLoaded
        ? (dataTargets?.manual?.periodCounts ?? [])
        : [];

    const orderedTargetSubtypes = useMemo(() => {
        const values = TARGET_SUBTYPES.map((option) => option.value);

        return [...new Set([targetSubtype, ...values])].filter(Boolean);
    }, [targetSubtype]);

    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [dialogError, setDialogError] = useState("");

    const calculatedData = useMemo(() => {
        if (!sanitizedHotelID) return null;

        return calculateLastYear(
            dataBookings,
            dataSimple,
            dataTargets,
            chartFromDate,
            chartToDate,
            dateGranularity,
            targetType,
            sanitizedHotelID,
        );
    }, [
        dataBookings,
        dataSimple,
        dataTargets,
        chartFromDate,
        chartToDate,
        dateGranularity,
        targetType,
        sanitizedHotelID,
    ]);

    // const [manualTargetsCounts, setManualTargetsCounts] = useState([]);
    // const [periodCounts, setPeriodCounts] = useState([]);

    const [draftPoint, setDraftPoint] = useState(null);
    const [draftPointUpdate, setDraftPointUpdate] = useState(null);
    const [draftPeriodUpdate, setDraftPeriodUpdate] = useState(null);

    const [isPointDialogOpen, setIsPointDialogOpen] = useState(false);
    const [isPointDialogUpdateOpen, setIsPointDialogUpdateOpen] =
        useState(false);
    const [isPointDialogUpdatePeriodOpen, setIsPointDialogUpdatePeriodOpen] =
        useState(false);

    useEffect(() => {
        dispatch(clear_ManualTargets());

        setDraftPoint(null);
        setDraftPointUpdate(null);
        setDraftPeriodUpdate(null);
        setIsPointDialogOpen(false);
        setIsPointDialogUpdateOpen(false);
        setIsPointDialogUpdatePeriodOpen(false);
        setDialogError("");
        setFeedback(null);
    }, [sanitizedHotelID, dispatch]);

    useEffect(() => {
        setChartFromDate((previous) =>
            normalizeRangeStart(previous, dateGranularity),
        );
        setChartToDate((previous) =>
            normalizeRangeEnd(previous, dateGranularity),
        );
        setValidFrom((previous) =>
            normalizeValidFromDate(previous, dateGranularity).format(
                "YYYY-MM-DD",
            ),
        );
        setDraftPoint(null);
        setDraftPointUpdate(null);
        setDraftPeriodUpdate(null);
        setIsPointDialogOpen(false);
        setIsPointDialogUpdateOpen(false);
        setIsPointDialogUpdatePeriodOpen(false);
        dispatch(clear_ManualTargets());
        // setManualTargetsCounts([]);
        // setPeriodCounts([]);
    }, [dateGranularity, dispatch]);

    const validateBaseFilters = useCallback(() => {
        if (!sanitizedHotelID) {
            setFeedback({
                type: "error",
                text: "Hotel ID is missing.",
            });
            return false;
        }

        if (!targetType.trim()) {
            setFeedback({
                type: "error",
                text: "Target type is required.",
            });
            return false;
        }

        if (!targetSubtype.trim()) {
            setFeedback({
                type: "error",
                text: "Target subtype is required.",
            });
            return false;
        }

        return true;
    }, [sanitizedHotelID, targetType, targetSubtype]);

    const loadTargets = useCallback(
        async ({ force = false } = {}) => {
            if (
                !sanitizedHotelID ||
                !targetType.trim() ||
                (visualizerMode === "chart" && !targetSubtype.trim())
            ) {
                return;
            }

            if (
                !force &&
                dataTargets?.manual?.hotelID === sanitizedHotelID &&
                dataTargets?.manual?.filtersKey === currentManualFiltersKey &&
                ["loading", "loaded"].includes(dataTargets?.manual?.status)
            ) {
                return;
            }

            setLoading(true);
            setFeedback(null);

            dispatch(
                loading_ManualTargets({
                    hotelID: sanitizedHotelID,
                    filtersKey: currentManualFiltersKey,
                }),
            );

            try {
                const response = await Axios.get(MANUAL_TARGETS_URL, {
                    headers,
                    params: {
                        hotel_id: sanitizedHotelID,
                        target_type: targetType.trim(),
                        ...(visualizerMode === "chart" && {
                            target_subtype: targetSubtype.trim(),
                        }),
                        date_granularity: dateGranularity,
                        include_periods: true,
                    },
                });

                const payload = response?.data ?? {};

                dispatch(
                    load_ManualTargets({
                        hotelID: sanitizedHotelID,
                        filtersKey: currentManualFiltersKey,
                        manualTargets: payload.data ?? [],
                        periodRows: payload.period ?? [],
                        manualTargetsCounts: payload.counts ?? [],
                        periodCounts: payload.period_counts ?? [],
                        targetsTree: payload.targets_tree ?? {},
                    }),
                );
            } catch (error) {
                const nextMessage = getErrorMessage(
                    error,
                    "Could not load targets.",
                );

                dispatch(
                    error_ManualTargets({
                        hotelID: sanitizedHotelID,
                        filtersKey: currentManualFiltersKey,
                        error: nextMessage,
                    }),
                );

                setFeedback({
                    type: "error",
                    text: nextMessage,
                });
            } finally {
                setLoading(false);
            }
        },
        [
            currentManualFiltersKey,
            dataTargets?.manual?.filtersKey,
            dataTargets?.manual?.hotelID,
            dataTargets?.manual?.status,
            dateGranularity,
            dispatch,
            headers,
            sanitizedHotelID,
            targetSubtype,
            targetType,
            visualizerMode,
        ],
    );

    const countsSections = useMemo(() => {
        const buildManualPointsSection = () => {
            const groupedByDateType = manualTargetsCounts.reduce((acc, row) => {
                const dateType = row.DateGranularity;
                const count = Number(row.PeriodsCount ?? 0);
                const combinationKey = `${row.TargetType}__${row.TargetSubType}`;

                if (!acc[dateType]) {
                    acc[dateType] = {
                        key: `manual-points-${dateType}`,
                        label: getDateGranularityLabel(dateType),
                        count: 0,
                        combinations: {},
                    };
                }

                acc[dateType].count += count;

                if (!acc[dateType].combinations[combinationKey]) {
                    acc[dateType].combinations[combinationKey] = {
                        key: `manual-points-${dateType}-${combinationKey}`,
                        label: `${getTargetTypeLabel(row.TargetType)} · ${getTargetSubtypeLabel(row.TargetSubType)}`,
                        count: 0,
                        selection: {
                            targetType: row.TargetType,
                            targetSubtype: row.TargetSubType,
                            dateGranularity: row.DateGranularity,
                        },
                    };
                }

                acc[dateType].combinations[combinationKey].count += count;

                return acc;
            }, {});

            const children = Object.values(groupedByDateType)
                .sort(
                    (left, right) =>
                        (TARGET_DATE_TYPE_ORDER[left.label] ?? 999) -
                        (TARGET_DATE_TYPE_ORDER[right.label] ?? 999),
                )
                .map((group) => ({
                    key: group.key,
                    label: group.label,
                    count: group.count,
                    children: Object.values(group.combinations).sort(
                        (left, right) => left.label.localeCompare(right.label),
                    ),
                }));

            return {
                key: "manual-points-root",
                label: "Manual points",
                count: manualTargetsCounts.reduce(
                    (acc, row) => acc + Number(row.PeriodsCount ?? 0),
                    0,
                ),
                children,
            };
        };

        const buildTargetPeriodsSection = () => {
            const groupedByDateType = periodCounts.reduce((acc, row) => {
                const dateType = row.DateGranularity;
                const count = Number(row.PeriodsCount ?? 0);
                const combinationKey = `${row.TargetType}__${row.TargetSubType}__${row.TargetMode}`;

                if (!acc[dateType]) {
                    acc[dateType] = {
                        key: `target-periods-${dateType}`,
                        label: getDateGranularityLabel(dateType),
                        count: 0,
                        combinations: {},
                    };
                }

                acc[dateType].count += count;

                if (!acc[dateType].combinations[combinationKey]) {
                    acc[dateType].combinations[combinationKey] = {
                        key: `target-periods-${dateType}-${combinationKey}`,
                        label: `${getTargetTypeLabel(row.TargetType)} · ${getTargetSubtypeLabel(row.TargetSubType)} · ${getModeLabel(row.TargetMode)}`,
                        count: 0,
                        selection: {
                            targetType: row.TargetType,
                            targetSubtype: row.TargetSubType,
                            dateGranularity: row.DateGranularity,
                            targetMode: row.TargetMode,
                        },
                    };
                }

                acc[dateType].combinations[combinationKey].count += count;

                return acc;
            }, {});

            const children = Object.values(groupedByDateType)
                .sort(
                    (left, right) =>
                        (TARGET_DATE_TYPE_ORDER[left.label] ?? 999) -
                        (TARGET_DATE_TYPE_ORDER[right.label] ?? 999),
                )
                .map((group) => ({
                    key: group.key,
                    label: group.label,
                    count: group.count,
                    children: Object.values(group.combinations).sort(
                        (left, right) => left.label.localeCompare(right.label),
                    ),
                }));

            return {
                key: "target-periods-root",
                label: "Target periods",
                count: periodCounts.reduce(
                    (acc, row) => acc + Number(row.PeriodsCount ?? 0),
                    0,
                ),
                children,
            };
        };

        return [buildManualPointsSection(), buildTargetPeriodsSection()];
    }, [periodCounts, manualTargetsCounts]);

    useEffect(() => {
        if (!sanitizedHotelID) return;

        const timeoutId = setTimeout(() => {
            loadTargets();
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [
        sanitizedHotelID,
        targetType,
        targetSubtype,
        dateGranularity,
        visualizerMode,
        loadTargets,
    ]);

    const handleCreatePeriod = useCallback(async () => {
        if (!validateBaseFilters()) return;

        const parsedValidFrom = normalizeValidFromDate(
            validFrom,
            dateGranularity,
        );

        if (!parsedValidFrom || !parsedValidFrom.isValid()) {
            setFeedback({
                type: "error",
                text: "Valid from is required.",
            });
            return;
        }

        setLoading(true);
        setFeedback(null);

        try {
            await Axios.post(
                MANUAL_TARGET_PERIODS_URL,
                {
                    hotel_id: sanitizedHotelID,
                    valid_from: parsedValidFrom.format("YYYY-MM-DD"),
                    date_granularity: dateGranularity,
                    target_type: targetType.trim(),
                    target_subtype: targetSubtype.trim(),
                    target_mode: getModeApiValue(targetMode),
                    percentage_n: Number(percentageN),
                },
                { headers },
            );

            setFeedback({
                type: "success",
                text: "Target period stored.",
                autoCloseMs: 5000,
            });

            await loadTargets({ force: true });
        } catch (error) {
            setFeedback({
                type: "error",
                text: getErrorMessage(error, "Could not store target period."),
            });
        } finally {
            setLoading(false);
        }
    }, [
        headers,
        hotelID,
        loadTargets,
        percentageN,
        dateGranularity,
        targetMode,
        targetSubtype,
        targetType,
        validFrom,
        validateBaseFilters,
    ]);

    const handleDeletePeriod = useCallback(
        async (row) => {
            setLoading(true);
            setFeedback(null);

            try {
                await Axios.delete(MANUAL_TARGET_PERIODS_URL, {
                    headers,
                    data: {
                        hotel_id: SanitizeHotelID(row.HotelID ?? hotelID),
                        valid_from: normalizeValidFromDate(
                            row.ValidFrom,
                            row.DateGranularity || dateGranularity,
                        ).format("YYYY-MM-DD"),
                        target_type: row.TargetType,
                        target_subtype: row.TargetSubType,
                        date_granularity: row.DateGranularity,
                    },
                });

                setFeedback({
                    type: "success",
                    text: "Target period deleted.",
                    autoCloseMs: 5000,
                });

                await loadTargets({ force: true });
            } catch (error) {
                setFeedback({
                    type: "error",
                    text: getErrorMessage(
                        error,
                        "Could not delete target period.",
                    ),
                });
            } finally {
                setLoading(false);
            }
        },
        [headers, hotelID, loadTargets, dateGranularity],
    );

    const handleDeleteManualTarget = useCallback(
        async (row) => {
            setLoading(true);
            setFeedback(null);

            try {
                await Axios.delete(MANUAL_TARGETS_URL, {
                    headers,
                    data: {
                        hotel_id: SanitizeHotelID(row.HotelID ?? hotelID),
                        target_date: normalizePointDate(
                            row.TargetDate,
                            row.DateGranularity || dateGranularity,
                        ).format("YYYY-MM-DD"),
                        target_type: row.TargetType,
                        target_subtype: row.TargetSubType,
                        date_granularity: row.DateGranularity,
                    },
                });

                setFeedback({
                    type: "success",
                    text: "Manual point deleted.",
                    autoCloseMs: 5000,
                });

                await loadTargets({ force: true });
            } catch (error) {
                setFeedback({
                    type: "error",
                    text: getErrorMessage(
                        error,
                        "Could not delete manual point.",
                    ),
                });
            } finally {
                setLoading(false);
            }
        },
        [headers, hotelID, loadTargets, dateGranularity],
    );

    const handleSaveDraftPoint = useCallback(async () => {
        if (!draftPoint) return;
        if (!validateBaseFilters()) return;
        if (Number(draftPoint.targetValue) <= 0) {
            setDraftPoint((current) => ({
                ...current,
                targetValue: Math.abs(Number(current.targetValue)),
            }));
            setDialogError("Target value must be greater than 0.");
            return;
        }

        if (!userIdForTargets) {
            const nextMessage = "User ID is missing.";
            setDialogError(nextMessage);
            setFeedback({
                type: "error",
                text: nextMessage,
            });
            return;
        }

        setLoading(true);
        setDialogError("");
        setFeedback(null);

        try {
            await Axios.post(
                MANUAL_TARGETS_URL,
                {
                    hotel_id: sanitizedHotelID,
                    target_date: draftPoint.targetDate,
                    date_granularity: dateGranularity,
                    target_type: (draftPoint.targetType ?? targetType).trim(),
                    target_subtype: (
                        draftPoint.targetSubtype ?? targetSubtype
                    ).trim(),
                    target_value: Number(draftPoint.targetValue),
                    user_id: userIdForTargets,
                },
                { headers },
            );

            setIsPointDialogOpen(false);
            setDraftPoint(null);
            setDialogError("");

            setFeedback({
                type: "success",
                text: "Manual point stored.",
                autoCloseMs: 5000,
            });

            await loadTargets({ force: true });
        } catch (error) {
            const rawMessage = getErrorMessage(
                error,
                "Could not store manual point.",
            );

            const nextMessage =
                rawMessage === "Input payload validation failed"
                    ? "Could not store manual point. Please check hotel, type, subtype and value."
                    : rawMessage;

            setDialogError(nextMessage);
            setFeedback({
                type: "error",
                text: nextMessage,
            });
        } finally {
            setLoading(false);
        }
    }, [
        draftPoint,
        headers,
        loadTargets,
        sanitizedHotelID,
        dateGranularity,
        targetSubtype,
        targetType,
        userIdForTargets,
        validateBaseFilters,
    ]);

    const visibleManualTargets = useMemo(() => {
        if (savedTargetsScope === "all") return manualTargets;

        const from = normalizeRangeStart(chartFromDate, dateGranularity);
        const to = normalizeRangeEnd(chartToDate, dateGranularity);

        return manualTargets.filter((row) => {
            const date = normalizePointDate(
                row.TargetDate,
                row.DateGranularity || dateGranularity,
            );

            return (
                date && !date.isBefore(from, "day") && !date.isAfter(to, "day")
            );
        });
    }, [
        savedTargetsScope,
        manualTargets,
        chartFromDate,
        chartToDate,
        dateGranularity,
    ]);
    const visiblePeriodRows = useMemo(() => {
        const from = normalizeRangeStart(chartFromDate, dateGranularity);
        const to = normalizeRangeEnd(chartToDate, dateGranularity);

        const sorted = [...periodRows]
            .map((row) => ({
                ...row,
                parsedValidFrom: normalizeValidFromDate(
                    row.ValidFrom,
                    row.DateGranularity || dateGranularity,
                ),
            }))
            .filter((row) => !!row.parsedValidFrom)
            .sort((left, right) =>
                left.parsedValidFrom.isBefore(right.parsedValidFrom, "day")
                    ? -1
                    : 1,
            );

        if (savedTargetsScope === "all") return sorted;

        const rowsUntilRangeEnd = sorted.filter(
            (row) => !row.parsedValidFrom.isAfter(to, "day"),
        );

        let lastBeforeRange = null;
        const rowsInsideRange = [];

        rowsUntilRangeEnd.forEach((row) => {
            if (row.parsedValidFrom.isBefore(from, "day")) {
                lastBeforeRange = row;
            } else {
                rowsInsideRange.push(row);
            }
        });

        return lastBeforeRange
            ? [lastBeforeRange, ...rowsInsideRange]
            : rowsInsideRange;
    }, [
        savedTargetsScope,
        chartFromDate,
        chartToDate,
        periodRows,
        dateGranularity,
    ]);

    const chartRangePickerFromDate = useMemo(
        () => normalizeRangeStart(chartFromDate, dateGranularity),
        [chartFromDate, dateGranularity],
    );

    const chartRangePickerToDate = useMemo(
        () => normalizeRangeEnd(chartToDate, dateGranularity),
        [chartToDate, dateGranularity],
    );

    const handleOpenDraftDialog = (nextDraftPoint) => {
        setDialogError("");
        setDraftPoint({
            ...nextDraftPoint,
            targetType: nextDraftPoint.targetType ?? targetType,
            targetSubtype: nextDraftPoint.targetSubtype ?? targetSubtype,
        });
        setIsPointDialogOpen(true);
    };

    const handleOpenDraftUpdateDialog = useCallback(
        (row) => {
            const normalizedDate = normalizePointDate(
                row.TargetDate,
                row.DateGranularity || dateGranularity,
            );

            if (!normalizedDate) return;

            setDialogError("");
            setDraftPointUpdate({
                hotelId: SanitizeHotelID(row.HotelID ?? hotelID),
                originalTargetDate: normalizedDate.format("YYYY-MM-DD"),
                targetDate: normalizedDate.format("YYYY-MM-DD"),
                dateGranularity: row.DateGranularity || dateGranularity,
                targetType: row.TargetType,
                targetSubtype: row.TargetSubType,
                targetValue: Math.round(Number(row.TargetValue ?? 0)),
                label: formatDisplayDate(
                    normalizedDate,
                    row.DateGranularity || dateGranularity,
                ),
            });
            setIsPointDialogUpdateOpen(true);
        },
        [hotelID, dateGranularity],
    );

    const handleOpenDraftUpdatePeriodDialog = useCallback(
        (row) => {
            const normalizedValidFrom = normalizeValidFromDate(
                row.ValidFrom,
                row.DateGranularity || dateGranularity,
            );

            if (!normalizedValidFrom) return;

            setDialogError("");
            setDraftPeriodUpdate({
                hotelId: SanitizeHotelID(row.HotelID ?? hotelID),
                originalValidFrom: normalizedValidFrom.format("YYYY-MM-DD"),
                validFrom: normalizedValidFrom.format("YYYY-MM-DD"),
                dateGranularity: row.DateGranularity || dateGranularity,
                targetType: row.TargetType,
                targetSubtype: row.TargetSubType,
                targetMode: getModeFormValue(row.TargetMode),
                percentageN: Number(row.PercentageN ?? 0),
            });
            setIsPointDialogUpdatePeriodOpen(true);
        },
        [hotelID, dateGranularity],
    );

    const handleCloseDraftDialog = () => {
        setDialogError("");
        setDraftPoint(null);
        setIsPointDialogOpen(false);
    };

    const handleCloseDraftUpdateDialog = () => {
        setDialogError("");
        setDraftPointUpdate(null);
        setIsPointDialogUpdateOpen(false);
    };

    const handleCloseDraftUpdatePeriodDialog = () => {
        setDialogError("");
        setDraftPeriodUpdate(null);
        setIsPointDialogUpdatePeriodOpen(false);
    };

    const handleUpdateDraftPoint = useCallback(async () => {
        if (!draftPointUpdate) return;
        if (Number(draftPointUpdate.targetValue) <= 0) {
            setDraftPointUpdate((current) => ({
                ...current,
                targetValue: Math.abs(Number(current.targetValue)),
            }));
            setDialogError("Target value must be greater than 0.");
            return;
        }

        if (!validateBaseFilters()) return;

        if (!userIdForTargets) {
            const nextMessage = "User ID is missing.";
            setDialogError(nextMessage);
            setFeedback({
                type: "error",
                text: nextMessage,
            });
            return;
        }

        setLoading(true);
        setDialogError("");
        setFeedback(null);

        try {
            const parsedTargetDate = normalizePointDate(
                draftPointUpdate.targetDate,
                draftPointUpdate.dateGranularity,
            );

            if (!parsedTargetDate || !parsedTargetDate.isValid()) {
                const nextMessage = "Date is required.";
                setDialogError(nextMessage);
                setFeedback({
                    type: "error",
                    text: nextMessage,
                });
                return;
            }

            const nextTargetDate = parsedTargetDate.format("YYYY-MM-DD");

            // Manual point date is part of the record key, so changing it needs removing the old key first.
            if (draftPointUpdate.originalTargetDate !== nextTargetDate) {
                await Axios.delete(MANUAL_TARGETS_URL, {
                    headers,
                    data: {
                        hotel_id: draftPointUpdate.hotelId,
                        target_date: draftPointUpdate.originalTargetDate,
                        target_type: draftPointUpdate.targetType,
                        target_subtype: draftPointUpdate.targetSubtype,
                        date_granularity: draftPointUpdate.dateGranularity,
                    },
                });
            }

            await Axios.post(
                MANUAL_TARGETS_URL,
                {
                    hotel_id: draftPointUpdate.hotelId,
                    target_date: nextTargetDate,
                    date_granularity: draftPointUpdate.dateGranularity,
                    target_type: draftPointUpdate.targetType,
                    target_subtype: draftPointUpdate.targetSubtype,
                    target_value: Number(draftPointUpdate.targetValue),
                    user_id: userIdForTargets,
                },
                { headers },
            );

            setIsPointDialogUpdateOpen(false);
            setDraftPointUpdate(null);
            setDialogError("");

            setFeedback({
                type: "success",
                text: "Manual point updated.",
                autoCloseMs: 5000,
            });

            await loadTargets({ force: true });
        } catch (error) {
            const rawMessage = getErrorMessage(
                error,
                "Could not update manual point.",
            );

            const nextMessage =
                rawMessage === "Input payload validation failed"
                    ? "Could not update manual point. Please check hotel, type, subtype, date and value."
                    : rawMessage;

            setDialogError(nextMessage);
            setFeedback({
                type: "error",
                text: nextMessage,
            });
        } finally {
            setLoading(false);
        }
    }, [
        draftPointUpdate,
        headers,
        loadTargets,
        userIdForTargets,
        validateBaseFilters,
    ]);

    const handleUpdateDraftPeriod = useCallback(async () => {
        if (!draftPeriodUpdate) return;

        if (!validateBaseFilters()) return;

        const parsedValidFrom = normalizeValidFromDate(
            draftPeriodUpdate.validFrom,
            draftPeriodUpdate.dateGranularity,
        );

        if (!parsedValidFrom || !parsedValidFrom.isValid()) {
            const nextMessage = "Valid from is required.";
            setDialogError(nextMessage);
            setFeedback({
                type: "error",
                text: nextMessage,
            });
            return;
        }

        setLoading(true);
        setDialogError("");
        setFeedback(null);

        try {
            const nextValidFrom = parsedValidFrom.format("YYYY-MM-DD");

            if (draftPeriodUpdate.originalValidFrom !== nextValidFrom) {
                await Axios.delete(MANUAL_TARGET_PERIODS_URL, {
                    headers,
                    data: {
                        hotel_id: draftPeriodUpdate.hotelId,
                        valid_from: draftPeriodUpdate.originalValidFrom,
                        target_type: draftPeriodUpdate.targetType,
                        target_subtype: draftPeriodUpdate.targetSubtype,
                        date_granularity: draftPeriodUpdate.dateGranularity,
                    },
                });
            }

            await Axios.post(
                MANUAL_TARGET_PERIODS_URL,
                {
                    hotel_id: draftPeriodUpdate.hotelId,
                    valid_from: nextValidFrom,
                    date_granularity: draftPeriodUpdate.dateGranularity,
                    target_type: draftPeriodUpdate.targetType,
                    target_subtype: draftPeriodUpdate.targetSubtype,
                    target_mode: getModeApiValue(draftPeriodUpdate.targetMode),
                    percentage_n: Number(draftPeriodUpdate.percentageN),
                },
                { headers },
            );

            setIsPointDialogUpdatePeriodOpen(false);
            setDraftPeriodUpdate(null);
            setDialogError("");

            setFeedback({
                type: "success",
                text: "Target period updated.",
                autoCloseMs: 5000,
            });

            await loadTargets({ force: true });
        } catch (error) {
            const nextMessage = getErrorMessage(
                error,
                "Could not update target period.",
            );

            setDialogError(nextMessage);
            setFeedback({
                type: "error",
                text: nextMessage,
            });
        } finally {
            setLoading(false);
        }
    }, [draftPeriodUpdate, headers, loadTargets, validateBaseFilters]);

    useEffect(() => {
        if (!feedback || !feedback.autoCloseMs) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            setFeedback((current) => {
                if (
                    current?.text === feedback.text &&
                    current?.autoCloseMs === feedback.autoCloseMs
                ) {
                    return null;
                }

                return current;
            });
        }, feedback.autoCloseMs);

        return () => clearTimeout(timeoutId);
    }, [feedback]);

    const handleSelectCountNode = useCallback((node) => {
        if (!node?.selection) return;

        setTargetType(node.selection.targetType);
        setTargetSubtype(node.selection.targetSubtype);
        setDateGranularity(node.selection.dateGranularity);

        if (node.selection.targetMode) {
            setTargetMode(getModeFormValue(node.selection.targetMode));
        }
    }, []);

    const currentCountSelection = useMemo(() => {
        return {
            targetType,
            targetSubtype,
            dateGranularity,
            targetMode,
        };
    }, [targetType, targetSubtype, dateGranularity, targetMode]);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <DataLoader Reservations SimpleData />
            <Stack spacing={2}>
                <Typography variant="h4">Targets</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Targets define expected values for a selected metric, date
                    basis, and granularity. <br />
                    <br />
                    <strong> Percentage</strong> uses last year's value for each
                    period, adjusted by the configured percentage. <br />
                    <strong> Manual</strong> uses only the values entered
                    manually for specific dates. <br />
                    <strong> Combined</strong> uses manual values where they
                    exist and percentage-based values for the remaining dates.{" "}
                    <br />
                    <br />
                    <strong> Periods</strong> define when a target configuration
                    starts. Each period starts on a “Valid From” date and
                    remains active until the next period begins.
                </Typography>

                {feedback ? (
                    <Alert
                        severity={feedback.type}
                        onClose={() => setFeedback(null)}
                    >
                        {feedback.text}
                    </Alert>
                ) : null}
            </Stack>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "flex-end" }}
                sx={{ my: 2 }}
            >
                <DateRangePicker
                    title="Chart range"
                    fromDateProps={{ value: chartRangePickerFromDate }}
                    toDateProps={{ value: chartRangePickerToDate }}
                    onChangeFromDate={(value) =>
                        setChartFromDate(
                            normalizeRangeStart(value, dateGranularity),
                        )
                    }
                    onChangeToDate={(value) =>
                        setChartToDate(
                            normalizeRangeEnd(value, dateGranularity),
                        )
                    }
                    pickByMonthYear={
                        dateGranularity === TARGET_GRANULARITY_MONTH
                    }
                    pickByYear={dateGranularity === TARGET_GRANULARITY_YEAR}
                />

                <Stack>
                    <Typography>Target type</Typography>
                    <TextField
                        value={targetType}
                        select
                        onChange={(event) => setTargetType(event.target.value)}
                        size="small"
                    >
                        {TARGET_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                <Stack>
                    <Typography>Target subtype</Typography>
                    <TextField
                        select
                        value={targetSubtype}
                        onChange={(event) =>
                            setTargetSubtype(event.target.value)
                        }
                        size="small"
                    >
                        {TARGET_SUBTYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {getTargetSubtypeLabel(option.value)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                <Stack>
                    <Typography>Granularity</Typography>
                    <TextField
                        select
                        value={dateGranularity}
                        onChange={(event) =>
                            setDateGranularity(event.target.value)
                        }
                        size="small"
                    >
                        {TARGET_DATE_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                <ManualTargetsViewModeToggle
                    value={visualizerMode}
                    onChange={setVisualizerMode}
                />

                {loading && <CircularProgress size={20} />}
            </Stack>
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <Stack spacing={2}>
                    <ManualTargetsVisualizer
                        loading={
                            loading ||
                            (!!sanitizedHotelID &&
                                !hasCurrentManualTargetsLoaded)
                        }
                        viewMode={visualizerMode}
                        chartFromDate={chartFromDate}
                        chartToDate={chartToDate}
                        dateGranularity={dateGranularity}
                        targetType={targetType}
                        targetSubtype={targetSubtype}
                        targetSubtypes={orderedTargetSubtypes}
                        manualTargets={manualTargets}
                        periodRows={periodRows}
                        calculatedData={calculatedData}
                        validFrom={validFrom}
                        percentageN={percentageN}
                        sanitizedHotelID={sanitizedHotelID}
                        onCreatePoint={handleOpenDraftDialog}
                        onEditPoint={handleOpenDraftUpdateDialog}
                        onDeletePoint={handleDeleteManualTarget}
                    />

                    {/* New Period */}
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems={{ xs: "stretch", md: "flex-end" }}
                        justifyContent="flex-start"
                    >
                        <Stack>
                            <Typography>Valid from</Typography>
                            <ValidFromPicker
                                value={validFrom}
                                onChange={setValidFrom}
                                dateGranularity={dateGranularity}
                            />
                        </Stack>

                        <Stack>
                            <Typography>Target mode</Typography>
                            <TextField
                                select
                                value={targetMode}
                                onChange={(event) =>
                                    setTargetMode(event.target.value)
                                }
                                size="small"
                            >
                                {TARGET_MODES.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        <Stack sx={{ width: "160px" }}>
                            <Typography>% from last year</Typography>
                            <TextField
                                type="number"
                                value={percentageN}
                                onChange={(event) =>
                                    setPercentageN(Number(event.target.value))
                                }
                                size="small"
                            />
                        </Stack>

                        <Button
                            variant="contained"
                            onClick={handleCreatePeriod}
                            disabled={loading}
                            sx={{ height: 40 }}
                        >
                            New target period
                        </Button>
                    </Stack>
                </Stack>
            </Container>
            <br />
            {/* Saved values */}
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Typography variant="h6" sx={{ minWidth: 0 }}>
                        Saved targets
                        <Box
                            component="span"
                            sx={{ fontSize: "0.8em", fontWeight: 400 }}
                        >
                            {" "}
                            ·{" "}
                            {
                                TARGET_DATE_TYPES.find(
                                    (item) => item.value === dateGranularity,
                                )?.label
                            }{" "}
                            ·{" "}
                            {
                                TARGET_TYPES.find(
                                    (item) => item.value === targetType,
                                )?.label
                            }{" "}
                            · {getTargetSubtypeLabel(targetSubtype)}
                        </Box>
                    </Typography>

                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={savedTargetsScope}
                        onChange={(event, value) => {
                            if (value) setSavedTargetsScope(value);
                        }}
                        sx={{
                            flexShrink: 0,
                            "& .MuiToggleButton-root": {
                                minWidth: 0,
                                px: 0.8,
                                py: 0.2,
                                fontSize: "0.7rem",
                                lineHeight: 1.4,
                                textTransform: "none",
                            },
                        }}
                    >
                        <ToggleButton value="range">Range</ToggleButton>
                        <ToggleButton value="all">All</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography
                            variant="subtitle1"
                            sx={{ mb: 1, fontWeight: 600 }}
                        >
                            Manual points
                        </Typography>

                        <Stack
                            spacing={0.75}
                            sx={{
                                maxHeight: 320,
                                overflowY: "auto",
                                pr: 0.5,
                            }}
                        >
                            {visibleManualTargets.length ? (
                                visibleManualTargets.map((row) => (
                                    <Box
                                        key={row.ManualTargetID}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            borderRadius: 1,
                                            px: 1,
                                            py: 0.5,
                                            minHeight: 36,
                                        }}
                                    >
                                        <Typography variant="body2" noWrap>
                                            {formatDisplayDate(
                                                row.TargetDate,
                                                row.DateGranularity,
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {round2(row.TargetValue)}
                                        </Typography>
                                        <Stack direction={"row"}>
                                            <IconButton
                                                onClick={() =>
                                                    handleOpenDraftUpdateDialog(
                                                        row,
                                                    )
                                                }
                                                size="small"
                                                sx={{ ml: 1, p: 0.5 }}
                                            >
                                                <Icons.Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() =>
                                                    handleDeleteManualTarget(
                                                        row,
                                                    )
                                                }
                                                size="small"
                                                sx={{ ml: 1, p: 0.5 }}
                                            >
                                                <Icons.Delete fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                ))
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No manual points loaded.
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    <Box>
                        <Typography
                            variant="subtitle1"
                            sx={{ mb: 1, fontWeight: 600 }}
                        >
                            Target periods
                        </Typography>

                        <Stack
                            spacing={1}
                            sx={{
                                maxHeight: 320,
                                overflowY: "auto",
                                pr: 0.5,
                            }}
                        >
                            {visiblePeriodRows.length ? (
                                visiblePeriodRows.map((row) => (
                                    <Box
                                        key={row.ManualTargetPeriodID}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            borderRadius: 1,
                                            px: 1,
                                            py: 0.5,
                                            minHeight: 36,
                                        }}
                                    >
                                        {/* <Box> */}
                                        <Typography variant="body2">
                                            {formatDisplayDate(
                                                row.ValidFrom,
                                                row.DateGranularity,
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {getModeLabel(row.TargetMode)} ·{" "}
                                            {Number(row.PercentageN ?? 0)}%
                                        </Typography>
                                        {/* </Box> */}
                                        <Stack direction={"row"}>
                                            <IconButton
                                                onClick={() =>
                                                    handleOpenDraftUpdatePeriodDialog(
                                                        row,
                                                    )
                                                }
                                                size="small"
                                                sx={{ ml: 1, p: 0.5 }}
                                            >
                                                <Icons.Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() =>
                                                    handleDeletePeriod(row)
                                                }
                                                size="small"
                                                sx={{ ml: 1, p: 0.5 }}
                                            >
                                                <Icons.Delete fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                ))
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No target periods loaded.
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </Container>
            <br />
            {/* Counts */}
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                        Counts for{" "}
                        {getHotelName(hotelID, hotels, false) || "hotel"}{" "}
                        lifetime
                    </Typography>

                    <Stack spacing={1.25}>
                        {countsSections.map((section) => (
                            <CountAccordionNode
                                key={section.key}
                                node={section}
                                onSelect={handleSelectCountNode}
                                currentSelection={currentCountSelection}
                            />
                        ))}
                    </Stack>
                </Box>
            </Container>
            {/* Manual Point Dialog CREATE */}
            <Dialog
                open={isPointDialogOpen}
                onClose={handleCloseDraftDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Confirm manual point</DialogTitle>

                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {dialogError ? (
                            <Alert severity="error">{dialogError}</Alert>
                        ) : null}
                        <TextField
                            label="Target type"
                            select
                            value={draftPoint?.targetType ?? targetType}
                            onChange={(event) =>
                                setDraftPoint((previous) => ({
                                    ...previous,
                                    targetType: event.target.value,
                                }))
                            }
                            size="small"
                        >
                            {TARGET_TYPES.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Target subtype"
                            select
                            value={draftPoint?.targetSubtype ?? targetSubtype}
                            onChange={(event) =>
                                setDraftPoint((previous) => ({
                                    ...previous,
                                    targetSubtype: event.target.value,
                                }))
                            }
                            size="small"
                        >
                            {TARGET_SUBTYPES.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {getTargetSubtypeLabel(option.value)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <ValidFromPicker
                            label="Date"
                            value={draftPoint?.targetDate}
                            onChange={(nextValue) =>
                                setDraftPoint((previous) => {
                                    if (!previous) return previous;

                                    return {
                                        ...previous,
                                        targetDate: nextValue,
                                        label: formatDisplayDate(
                                            nextValue,
                                            dateGranularity,
                                        ),
                                    };
                                })
                            }
                            dateGranularity={dateGranularity}
                        />

                        <TextField
                            label="Value"
                            type="number"
                            value={draftPoint?.targetValue ?? ""}
                            onChange={(event) =>
                                setDraftPoint((previous) => ({
                                    ...previous,
                                    targetValue: Math.round(
                                        Number(event.target.value),
                                    ),
                                }))
                            }
                            size="small"
                            inputProps={{ min: 1, step: 1 }}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDraftDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveDraftPoint}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Manual Point Dialog UPDATE */}
            <Dialog
                open={isPointDialogUpdateOpen}
                onClose={handleCloseDraftUpdateDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Edit manual point</DialogTitle>

                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {dialogError ? (
                            <Alert severity="error">{dialogError}</Alert>
                        ) : null}
                        <TextField
                            label="Target type"
                            value={getTargetTypeLabel(
                                draftPointUpdate?.targetType ?? "",
                            )}
                            size="small"
                            InputProps={{ readOnly: true }}
                        />

                        <TextField
                            label="Target subtype"
                            value={getTargetSubtypeLabel(
                                draftPointUpdate?.targetSubtype ?? "",
                            )}
                            size="small"
                            InputProps={{ readOnly: true }}
                        />
                        <ValidFromPicker
                            label="Date"
                            value={draftPointUpdate?.targetDate}
                            onChange={(nextValue) =>
                                setDraftPointUpdate((previous) => {
                                    if (!previous) return previous;

                                    return {
                                        ...previous,
                                        targetDate: nextValue,
                                        label: formatDisplayDate(
                                            nextValue,
                                            previous.dateGranularity ||
                                                dateGranularity,
                                        ),
                                    };
                                })
                            }
                            dateGranularity={
                                draftPointUpdate?.dateGranularity ||
                                dateGranularity
                            }
                        />

                        <TextField
                            label="Value"
                            type="number"
                            value={draftPointUpdate?.targetValue ?? ""}
                            onChange={(event) =>
                                setDraftPointUpdate((previous) => ({
                                    ...previous,
                                    targetValue: Math.round(
                                        Number(event.target.value),
                                    ),
                                }))
                            }
                            size="small"
                            inputProps={{ min: 1, step: 1 }}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => {
                            if (!draftPointUpdate) return;

                            handleDeleteManualTarget({
                                HotelID: draftPointUpdate.hotelId,
                                TargetDate: draftPointUpdate.originalTargetDate,
                                TargetType: draftPointUpdate.targetType,
                                TargetSubType: draftPointUpdate.targetSubtype,
                                DateGranularity:
                                    draftPointUpdate.dateGranularity,
                            });

                            handleCloseDraftUpdateDialog();
                        }}
                    >
                        Delete
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    <Button onClick={handleCloseDraftUpdateDialog}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleUpdateDraftPoint}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Period Dialog UPDATE */}
            <Dialog
                open={isPointDialogUpdatePeriodOpen}
                onClose={handleCloseDraftUpdatePeriodDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Edit target period</DialogTitle>

                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {dialogError ? (
                            <Alert severity="error">{dialogError}</Alert>
                        ) : null}

                        <ValidFromPicker
                            label="Valid from"
                            value={draftPeriodUpdate?.validFrom}
                            onChange={(nextValue) =>
                                setDraftPeriodUpdate((previous) => ({
                                    ...previous,
                                    validFrom: nextValue,
                                }))
                            }
                            dateGranularity={
                                draftPeriodUpdate?.dateGranularity ||
                                dateGranularity
                            }
                        />

                        <TextField
                            label="Target mode"
                            select
                            value={draftPeriodUpdate?.targetMode ?? "manual"}
                            onChange={(event) =>
                                setDraftPeriodUpdate((previous) => ({
                                    ...previous,
                                    targetMode: event.target.value,
                                }))
                            }
                            size="small"
                        >
                            {TARGET_MODES.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="% from last year"
                            type="number"
                            value={draftPeriodUpdate?.percentageN ?? 0}
                            onChange={(event) =>
                                setDraftPeriodUpdate((previous) => ({
                                    ...previous,
                                    percentageN: Number(event.target.value),
                                }))
                            }
                            size="small"
                        />
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDraftUpdatePeriodDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdateDraftPeriod}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ManualTargetsScreen;
